import type { TagDescription } from '@reduxjs/toolkit/query'
import { createApi } from '@reduxjs/toolkit/query/react'
import { BACKEND_API_URL } from '../consts/api'
import { extractResponseData, extractResponseError, fetchBaseQueryWithToken } from '../utils/api'

export const notificationsTag = 'Notifications' as const
export const notificationsUnreadCountTag = 'NotificationsUnreadCount' as const

export type NotificationProjectPayload = {
  id: number
  title: string
  /** Есть в ответе для комментариев и может отсутствовать в старых payload других типов */
  slug?: string
  image_url: string | null
}

export type NotificationSocialOrderPayload = {
  id: number
  title: string
  slug?: string
  image_url: string | null
  is_completed?: boolean
}

export type NotificationGrowthToolPayload = {
  id: number
  title: string
  slug?: string
  image_url: string | null
}

export type NotificationActorPayload = {
  id: number | null
  fullname: string
  avatar_url: string | null
  slug?: string | null
  deleted?: boolean
}

export type FollowedAuthorPayloadAuthor = {
  id: number
  slug: string
  fullname: string
  shortname: string
  avatar: string | null
}

export type FollowedAuthorPayloadFollower = {
  id: number
  fullname: string
  avatar: string | null
}

export type UserFollowedAuthorPayload = {
  author: FollowedAuthorPayloadAuthor | null
  last_follower: FollowedAuthorPayloadFollower | null
  followers_preview: FollowedAuthorPayloadFollower[]
  followers_count: number
  event_meta?: {
    timestamp?: string
  } & Record<string, unknown>
}

export type FollowedAuthorProjectPayload = {
  author: FollowedAuthorPayloadAuthor | null
  project: {
    id: number
    slug: string
    name: string
    image: string | null
  } | null
  event_meta?: {
    status_before?: string | null
    status_after?: string | null
    roles?: string[]
    needs?: string[]
    timestamp?: string
    time_bucket?: string
  } & Record<string, unknown>
}

export type JoinRequestCreatedPayload = {
  project: NotificationProjectPayload
  actor: NotificationActorPayload
  join_request: {
    id: number
    status: 'pending' | 'approved' | 'rejected' | string
    message?: string | null
  }
}

export type JoinRequestDecidedPayload = {
  project: NotificationProjectPayload
  actor: NotificationActorPayload
  join_request: {
    id: number
    status: 'approved' | 'rejected' | string
    decision_message?: string | null
  }
}

export type JoinRequestCanceledPayload = {
  project: NotificationProjectPayload
  actor: NotificationActorPayload
  join_request: {
    id: number
    message?: string | null
  }
}

export type InvitationCreatedPayload = {
  project: NotificationProjectPayload
  actor: NotificationActorPayload
  invitation: {
    id: number
    status: 'pending' | 'accepted' | 'declined' | 'cancelled' | string
    message?: string | null
  }
}

export type InvitationDecidedPayload = {
  project: NotificationProjectPayload
  actor: NotificationActorPayload
  invitation: {
    id: number
    status: 'accepted' | 'declined' | string
    response_message?: string | null
  }
}

export type InvitationCancelledPayload = {
  project: NotificationProjectPayload
  actor: NotificationActorPayload
  invitation: {
    id: number
    message?: string | null
  }
}

export type NotificationTeamPayload = {
  id: number
  /** Каталог команд (`TeamNotificationPayload::teamBlock`) */
  title?: string
  /** Мост project→team (`ProjectTeamInvitationNotificationPayload`) */
  name?: string
  slug?: string
  image_url: string | null
}

export type TeamInvitationCreatedPayload = {
  project: NotificationProjectPayload
  team: NotificationTeamPayload
  actor: NotificationActorPayload
  invitation: {
    id: number
    status: 'pending' | 'accepted' | 'declined' | 'cancelled' | string
    message?: string | null
  }
}

export type TeamInvitationDecidedPayload = {
  project: NotificationProjectPayload
  team: NotificationTeamPayload
  actor: NotificationActorPayload
  invitation: {
    id: number
    status: 'accepted' | 'declined' | string
    response_message?: string | null
  }
}

export type TeamInvitationCancelledPayload = {
  project: NotificationProjectPayload
  team: NotificationTeamPayload
  actor: NotificationActorPayload
  invitation: {
    id: number
    message?: string | null
  }
}

export type ProjectTeamRemovedPayload = {
  project: NotificationProjectPayload
  team: NotificationTeamPayload
  actor: NotificationActorPayload
  message?: string | null
}

export type SubscriptionCreatedPayload = {
  project: NotificationProjectPayload
  actor: NotificationActorPayload
}

export type SubscriptionDeletedPayload = {
  project: NotificationProjectPayload
  actor: NotificationActorPayload
}

export type TeamSubscriptionCreatedPayload = {
  team: NotificationTeamPayload
  actor: NotificationActorPayload
}

export type TeamSubscriptionDeletedPayload = {
  team: NotificationTeamPayload
  actor: NotificationActorPayload
}

export type TeamMemberLeftPayload = {
  team: NotificationTeamPayload
  actor: NotificationActorPayload
  message?: string | null
}

export type TeamMemberRemovedPayload = {
  team: NotificationTeamPayload
  actor: NotificationActorPayload
  member?: NotificationActorPayload
  message?: string | null
}

export type TeamJoinRequestCreatedPayload = {
  team: NotificationTeamPayload
  actor: NotificationActorPayload
  join_request: {
    id: number
    status: 'pending' | 'approved' | 'rejected' | string
    message?: string | null
  }
}

export type TeamJoinRequestDecidedPayload = {
  team: NotificationTeamPayload
  actor: NotificationActorPayload
  join_request: {
    id: number
    status: 'approved' | 'rejected' | string
    decision_message?: string | null
  }
}

export type TeamJoinRequestCanceledPayload = {
  team: NotificationTeamPayload
  actor: NotificationActorPayload
  join_request: {
    id: number
    message?: string | null
  }
}

export type TeamCatalogInvitationCreatedPayload = {
  team: NotificationTeamPayload
  actor: NotificationActorPayload
  invitation: {
    id: number
    status: 'pending' | 'accepted' | 'declined' | 'cancelled' | string
    message?: string | null
  }
}

export type TeamCatalogInvitationDecidedPayload = {
  team: NotificationTeamPayload
  actor: NotificationActorPayload
  invitation: {
    id: number
    status: 'accepted' | 'declined' | string
    response_message?: string | null
  }
}

export type TeamCatalogInvitationCancelledPayload = {
  team: NotificationTeamPayload
  actor: NotificationActorPayload
  invitation: {
    id: number
    message?: string | null
  }
}

export type TeamAdminPayload = {
  team: NotificationTeamPayload
  actor: NotificationActorPayload
}

export type ProjectAdminPayload = {
  project: NotificationProjectPayload
  actor: NotificationActorPayload
}

export type ProjectTeamSpaceChangedPayload = {
  project: NotificationProjectPayload
  actor: NotificationActorPayload
  change: 'links_updated' | 'card_created' | 'card_deleted' | string
  subject_user_id?: number | null
}

export type ProjectRestrictionAppliedPayload = {
  project: NotificationProjectPayload
  actor: NotificationActorPayload
  restriction: {
    level: 'full' | 'comments' | string
    reason?: string | null
  }
}

export type ProjectRestrictionRemovedPayload = {
  project: NotificationProjectPayload
  actor: NotificationActorPayload
}

export type ProjectLikeSummaryPayload = {
  project: NotificationProjectPayload
  likes_count: number
  actors_preview: NotificationActorPayload[]
  last_actor: NotificationActorPayload
}

/** In-app: комментарии / лайк комментария; `project` с бэкенда может быть `null` (удалённый проект). */
export type ProjectCommentNotificationPayload = {
  project: NotificationProjectPayload | null
  actor?: NotificationActorPayload | null
  comment_id?: number
  comment_type?: string
  project_id?: number
  preview?: string | null
  parent_comment_id?: number | null
  reply_to_comment_id?: number | null
}

export type SocialOrderCompletionChangedPayload = {
  social_order: NotificationSocialOrderPayload
  actor: NotificationActorPayload
  event_meta?: {
    is_completed?: boolean
  }
}

export type SocialOrderLinkedProjectCreatedPayload = {
  social_order: NotificationSocialOrderPayload
  project: NotificationProjectPayload
  actor: NotificationActorPayload
}

/** In-app: комментарии / лайк комментария к соцзаказу. */
export type SocialOrderCommentNotificationPayload = {
  social_order: NotificationSocialOrderPayload | null
  actor?: NotificationActorPayload | null
  comment_id?: number
  comment_type?: string
  social_order_id?: number
  preview?: string | null
  parent_comment_id?: number | null
  reply_to_comment_id?: number | null
}

export type SocialOrderLikeSummaryPayload = {
  social_order: NotificationSocialOrderPayload
  likes_count: number
  actors_preview: NotificationActorPayload[]
  last_actor: NotificationActorPayload
}

export type SocialOrderSubscriptionCreatedPayload = {
  social_order: NotificationSocialOrderPayload
  actor: NotificationActorPayload
}

export type SocialOrderSubscriptionDeletedPayload = {
  social_order: NotificationSocialOrderPayload
  actor: NotificationActorPayload
}

export type SocialOrderRestrictionAppliedPayload = {
  social_order: NotificationSocialOrderPayload
  actor: NotificationActorPayload
  restriction: {
    level: 'full' | 'comments' | string
    reason?: string | null
  }
}

export type SocialOrderRestrictionRemovedPayload = {
  social_order: NotificationSocialOrderPayload
  actor: NotificationActorPayload
}

export type GrowthToolRestrictionAppliedPayload = {
  growth_tool: NotificationGrowthToolPayload
  actor: NotificationActorPayload
  restriction: {
    level: 'full' | 'comments' | string
    reason?: string | null
  }
}

export type GrowthToolRestrictionRemovedPayload = {
  growth_tool: NotificationGrowthToolPayload
  actor: NotificationActorPayload
}

export type ProjectCommentNotificationResource =
  | ({
      type: 'project.comment.created'
      payload: ProjectCommentNotificationPayload
    } & NotificationBaseResource)
  | ({
      type: 'project.comment.replied'
      payload: ProjectCommentNotificationPayload
    } & NotificationBaseResource)
  | ({
      type: 'project.comment.liked'
      payload: ProjectCommentNotificationPayload
    } & NotificationBaseResource)

export type SocialOrderCommentNotificationResource =
  | ({
      type: 'social_order.comment.created'
      payload: SocialOrderCommentNotificationPayload
    } & NotificationBaseResource)
  | ({
      type: 'social_order.comment.replied'
      payload: SocialOrderCommentNotificationPayload
    } & NotificationBaseResource)
  | ({
      type: 'social_order.comment.liked'
      payload: SocialOrderCommentNotificationPayload
    } & NotificationBaseResource)

export type KnownNotificationResource =
  | ({
      type: 'project.join_request.created'
      payload: JoinRequestCreatedPayload
    } & NotificationBaseResource)
  | ({
      type: 'project.join_request.decided'
      payload: JoinRequestDecidedPayload
    } & NotificationBaseResource)
  | ({
      type: 'project.join_request.canceled'
      payload: JoinRequestCanceledPayload
    } & NotificationBaseResource)
  | ({
      type: 'project.invitation.created'
      payload: InvitationCreatedPayload
    } & NotificationBaseResource)
  | ({
      type: 'project.invitation.decided'
      payload: InvitationDecidedPayload
    } & NotificationBaseResource)
  | ({
      type: 'project.invitation.cancelled'
      payload: InvitationCancelledPayload
    } & NotificationBaseResource)
  | ({
      type: 'project.team_invitation.created'
      payload: TeamInvitationCreatedPayload
    } & NotificationBaseResource)
  | ({
      type: 'project.team_invitation.decided'
      payload: TeamInvitationDecidedPayload
    } & NotificationBaseResource)
  | ({
      type: 'project.team_invitation.cancelled'
      payload: TeamInvitationCancelledPayload
    } & NotificationBaseResource)
  | ({
      type: 'project.team.removed'
      payload: ProjectTeamRemovedPayload
    } & NotificationBaseResource)
  | ({
      type: 'project.subscription.created'
      payload: SubscriptionCreatedPayload
    } & NotificationBaseResource)
  | ({
      type: 'project.subscription.deleted'
      payload: SubscriptionDeletedPayload
    } & NotificationBaseResource)
  | ({
      type: 'team.subscription.created'
      payload: TeamSubscriptionCreatedPayload
    } & NotificationBaseResource)
  | ({
      type: 'team.subscription.deleted'
      payload: TeamSubscriptionDeletedPayload
    } & NotificationBaseResource)
  | ({
      type: 'team.member.left'
      payload: TeamMemberLeftPayload
    } & NotificationBaseResource)
  | ({
      type: 'team.member.removed'
      payload: TeamMemberRemovedPayload
    } & NotificationBaseResource)
  | ({
      type: 'team.member.removed.broadcast'
      payload: TeamMemberRemovedPayload
    } & NotificationBaseResource)
  | ({
      type: 'team.join_request.created'
      payload: TeamJoinRequestCreatedPayload
    } & NotificationBaseResource)
  | ({
      type: 'team.join_request.decided'
      payload: TeamJoinRequestDecidedPayload
    } & NotificationBaseResource)
  | ({
      type: 'team.join_request.canceled'
      payload: TeamJoinRequestCanceledPayload
    } & NotificationBaseResource)
  | ({
      type: 'team.invitation.created'
      payload: TeamCatalogInvitationCreatedPayload
    } & NotificationBaseResource)
  | ({
      type: 'team.invitation.decided'
      payload: TeamCatalogInvitationDecidedPayload
    } & NotificationBaseResource)
  | ({
      type: 'team.invitation.cancelled'
      payload: TeamCatalogInvitationCancelledPayload
    } & NotificationBaseResource)
  | ({
      type: 'team.admin.granted'
      payload: TeamAdminPayload
    } & NotificationBaseResource)
  | ({
      type: 'team.admin.revoked'
      payload: TeamAdminPayload
    } & NotificationBaseResource)
  | ({
      type: 'project.admin.granted'
      payload: ProjectAdminPayload
    } & NotificationBaseResource)
  | ({
      type: 'project.admin.revoked'
      payload: ProjectAdminPayload
    } & NotificationBaseResource)
  | ({
      type: 'project.team_space.changed'
      payload: ProjectTeamSpaceChangedPayload
    } & NotificationBaseResource)
  | ({
      type: 'project.restriction.applied'
      payload: ProjectRestrictionAppliedPayload
    } & NotificationBaseResource)
  | ({
      type: 'project.restriction.removed'
      payload: ProjectRestrictionRemovedPayload
    } & NotificationBaseResource)
  | ({
      type: 'project.member.removed'
      payload: ProjectRestrictionRemovedPayload
    } & NotificationBaseResource)
  | ({
      type: 'project.like.summary'
      payload: ProjectLikeSummaryPayload
    } & NotificationBaseResource)
  | ({
      type: 'user_followed_author'
      payload: UserFollowedAuthorPayload
    } & NotificationBaseResource)
  | ({
      type: 'user_followed_author_project_published'
      payload: FollowedAuthorProjectPayload
    } & NotificationBaseResource)
  | ({
      type: 'user_followed_author_project_status_changed'
      payload: FollowedAuthorProjectPayload
    } & NotificationBaseResource)
  | ({
      type: 'user_followed_author_project_recruitment_opened'
      payload: FollowedAuthorProjectPayload
    } & NotificationBaseResource)
  | ({
      type: 'user_followed_author_project_recruitment_updated'
      payload: FollowedAuthorProjectPayload
    } & NotificationBaseResource)
  | ({
      type: 'social_order.completion.changed'
      payload: SocialOrderCompletionChangedPayload
    } & NotificationBaseResource)
  | ({
      type: 'social_order.linked_project.created'
      payload: SocialOrderLinkedProjectCreatedPayload
    } & NotificationBaseResource)
  | ({
      type: 'social_order.linked_project.created.author'
      payload: SocialOrderLinkedProjectCreatedPayload
    } & NotificationBaseResource)
  | ({
      type: 'social_order.like.summary'
      payload: SocialOrderLikeSummaryPayload
    } & NotificationBaseResource)
  | ({
      type: 'social_order.subscription.created'
      payload: SocialOrderSubscriptionCreatedPayload
    } & NotificationBaseResource)
  | ({
      type: 'social_order.subscription.deleted'
      payload: SocialOrderSubscriptionDeletedPayload
    } & NotificationBaseResource)
  | ({
      type: 'social_order.restriction.applied'
      payload: SocialOrderRestrictionAppliedPayload
    } & NotificationBaseResource)
  | ({
      type: 'social_order.restriction.removed'
      payload: SocialOrderRestrictionRemovedPayload
    } & NotificationBaseResource)
  | ({
      type: 'growth_tool.restriction.applied'
      payload: GrowthToolRestrictionAppliedPayload
    } & NotificationBaseResource)
  | ({
      type: 'growth_tool.restriction.removed'
      payload: GrowthToolRestrictionRemovedPayload
    } & NotificationBaseResource)
  | ProjectCommentNotificationResource
  | SocialOrderCommentNotificationResource

export type UnknownNotificationResource = NotificationBaseResource & {
  type: string
  payload?: Record<string, any> | unknown
}

export type NotificationResource = KnownNotificationResource | UnknownNotificationResource

export type NotificationBaseResource = {
  id: number
  created_at: string
  read_at: string | null
  type: string
  actor_user_id?: number | null
  subject_kind?: string
  subject_id?: number
  title?: string | null
  message?: string | null
  is_action_required?: boolean
}

export type NotificationFeedScope = 'all' | 'replies' | 'requests' | 'social_orders' | 'team_space'

export const NOTIFICATION_FEED_SCOPES: NotificationFeedScope[] = [
  'all',
  'replies',
  'requests',
  'social_orders',
  'team_space',
]

export type GetNotificationsQuery = {
  scope?: NotificationFeedScope
  limit?: number
  unread?: 0 | 1
  cursor?: string | null
}

type NotificationsTagTypes = typeof notificationsTag | typeof notificationsUnreadCountTag

export const notificationFeedScopeTags = [
  { type: notificationsTag, id: 'all' },
  { type: notificationsTag, id: 'replies' },
  { type: notificationsTag, id: 'requests' },
  { type: notificationsTag, id: 'social_orders' },
  { type: notificationsTag, id: 'team_space' },
] as const satisfies readonly TagDescription<NotificationsTagTypes>[]

export const notificationScopeInvalidationTags: TagDescription<NotificationsTagTypes>[] = [
  ...notificationFeedScopeTags,
  { type: notificationsTag },
  { type: notificationsUnreadCountTag },
]

export type NotificationsListResponse = {
  items: NotificationResource[]
  next_cursor: string | null
}

export type NotificationsUnreadCountResponse = {
  unread_count: number
}

export type NotificationsMarkAllReadResponse = {
  updated: number
  unread_count: number
}

export type StreamTokenResponse = {
  stream_url: string
  stream_token: string
  expires_in: number
}

export type PushConfigResponse = {
  vapid_public_key: string | null
  enabled: boolean
}

export type RegisterPushSubscriptionBody = {
  endpoint: string
  keys: {
    p256dh: string
    auth: string
  }
}

export type UnregisterPushSubscriptionBody = {
  endpoint: string
}

function pickNextCursorFromNotificationsRoot(body: Record<string, unknown>): string | null {
  const meta = body.meta as Record<string, unknown> | undefined
  const fromMeta = meta?.next_cursor
  if (fromMeta !== undefined && fromMeta !== null && String(fromMeta) !== '') {
    return String(fromMeta)
  }
  return null
}

function parseNotificationsListResponse(response: unknown): NotificationsListResponse {
  const top = (response ?? {}) as Record<string, unknown>
  const nextCursorFromRoot = pickNextCursorFromNotificationsRoot(top)
  const payload: any = extractResponseData(response)

  // API: { data: [...], meta: { next_cursor } } — после unwrap payload = массив
  if (Array.isArray(payload)) {
    return { items: payload as NotificationResource[], next_cursor: nextCursorFromRoot }
  }

  // Запасной формат: вложенный data + meta
  if (payload && typeof payload === 'object') {
    const data = Array.isArray(payload.data) ? (payload.data as NotificationResource[]) : []
    const nextCursor =
      typeof payload.meta?.next_cursor === 'string'
        ? payload.meta.next_cursor
        : typeof payload.next_cursor === 'string'
          ? payload.next_cursor
          : typeof payload.nextCursor === 'string'
            ? payload.nextCursor
            : nextCursorFromRoot
    return { items: data, next_cursor: nextCursor }
  }

  return { items: [], next_cursor: null }
}

function parseUnreadCountResponse(response: unknown): NotificationsUnreadCountResponse {
  const payload: any = extractResponseData(response)
  if (typeof payload === 'number') return { unread_count: payload }
  if (payload && typeof payload === 'object') {
    const raw =
      payload.unread_count ??
      payload.unreadCount ??
      payload.count ??
      payload.unread ??
      0
    const n = Number(raw)
    return { unread_count: Number.isFinite(n) ? n : 0 }
  }
  return { unread_count: 0 }
}

export const notificationsApi = createApi({
  reducerPath: 'notificationsApi',
  baseQuery: fetchBaseQueryWithToken(BACKEND_API_URL),
  tagTypes: [notificationsTag, notificationsUnreadCountTag],
  endpoints: (builder) => ({
    getNotifications: builder.query<NotificationsListResponse, GetNotificationsQuery | void>({
      query: (arg) => {
        const q = arg ?? {}
        const params: Record<string, string> = {}
        if (q.scope) params.scope = q.scope
        if (q.limit != null) params.limit = String(q.limit)
        if (q.unread != null) params.unread = String(q.unread)
        if (q.cursor != null) params.cursor = String(q.cursor)
        return {
          url: `/notifications`,
          ...(Object.keys(params).length > 0 ? { params } : {}),
        }
      },
      transformResponse: (response: unknown) => parseNotificationsListResponse(response),
      transformErrorResponse: extractResponseError,
      providesTags: (_result, _error, arg) => [
        { type: notificationsTag, id: arg?.scope ?? 'all' },
      ],
    }),

    getNotificationsUnreadCount: builder.query<NotificationsUnreadCountResponse, void>({
      query: () => ({
        url: `/notifications/unread-count`,
      }),
      transformResponse: (response: unknown) => parseUnreadCountResponse(response),
      transformErrorResponse: extractResponseError,
      providesTags: [notificationsUnreadCountTag],
    }),

    markNotificationsRead: builder.mutation<unknown, number[]>({
      query: (ids) => ({
        url: `/notifications/read`,
        method: 'POST',
        body: { ids },
      }),
      transformResponse: (response: unknown) => extractResponseData(response),
      transformErrorResponse: extractResponseError,
      invalidatesTags: notificationScopeInvalidationTags,
    }),

    markAllNotificationsRead: builder.mutation<NotificationsMarkAllReadResponse, void>({
      query: () => ({
        url: `/notifications/read-all`,
        method: 'POST',
      }),
      transformResponse: (response: unknown) => {
        const payload: any = extractResponseData(response)
        if (payload && typeof payload === 'object') {
          return {
            updated: Number(payload.updated ?? 0),
            unread_count: Number(payload.unread_count ?? 0),
          }
        }
        return { updated: 0, unread_count: 0 }
      },
      transformErrorResponse: extractResponseError,
      invalidatesTags: notificationScopeInvalidationTags,
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          notificationsApi.util.updateQueryData('getNotificationsUnreadCount', undefined, () => ({
            unread_count: 0,
          })),
        )
        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      },
    }),

    createNotificationStreamToken: builder.mutation<StreamTokenResponse, void>({
      query: () => ({
        url: `/notifications/stream-token`,
        method: 'POST',
      }),
      transformResponse: (response: unknown) => extractResponseData(response) as StreamTokenResponse,
      transformErrorResponse: extractResponseError,
    }),

    getPushConfig: builder.query<PushConfigResponse, void>({
      query: () => ({
        url: `/notifications/push-config`,
      }),
      transformResponse: (response: unknown) => extractResponseData(response) as PushConfigResponse,
      transformErrorResponse: extractResponseError,
    }),

    registerPushSubscription: builder.mutation<void, RegisterPushSubscriptionBody>({
      query: (body) => ({
        url: `/notifications/push-subscriptions`,
        method: 'POST',
        body,
      }),
      transformResponse: () => undefined,
      transformErrorResponse: extractResponseError,
    }),

    unregisterPushSubscription: builder.mutation<void, UnregisterPushSubscriptionBody>({
      query: (body) => ({
        url: `/notifications/push-subscriptions`,
        method: 'DELETE',
        body,
      }),
      transformResponse: () => undefined,
      transformErrorResponse: extractResponseError,
    }),
  }),
})

export const {
  useGetNotificationsQuery,
  useLazyGetNotificationsQuery,
  useGetNotificationsUnreadCountQuery,
  useMarkNotificationsReadMutation,
  useMarkAllNotificationsReadMutation,
  useCreateNotificationStreamTokenMutation,
  useGetPushConfigQuery,
  useRegisterPushSubscriptionMutation,
  useUnregisterPushSubscriptionMutation,
} = notificationsApi

